import React, { useState, useEffect, useMemo } from "react";
import ClearIcon from '@mui/icons-material/Clear';
// Replaced MUI pickers with custom range picker
import DateRangePicker from "../ui/DatePicker";
import dayjs from 'dayjs';
import {
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  X,
  BarChart3,
  Edit,
  Send,
  Eye,
  PlusCircle,
  ChevronLeft,
  ChevronRight,
  Save,
  Trash2,
  MessageSquare,
  User,
  Calendar,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { LayoutGrid, LayoutList } from 'lucide-react';

import toast from "react-hot-toast";
import ApiService from "../../services/ApiService";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import useBillingStore from "../../store/useBillingStore";

function SupportQueries() {
  const { user } = useUser(); // Get current Clerk user
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const { billingInfo, fetchBillingInfo } = useBillingStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Helper function to get current user display name
  const getCurrentUserName = () => {
    if (!user) return "Unknown User";
    
    // Try different user name properties in order of preference
    if (user.fullName) return user.fullName;
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.firstName) return user.firstName;
    if (user.username) return user.username;
    if (user.emailAddresses?.[0]?.emailAddress) {
      // Extract name from email if no other name is available
      const email = user.emailAddresses[0].emailAddress;
      const nameFromEmail = email.split('@')[0].replace(/[._-]/g, ' ');
      return nameFromEmail.charAt(0).toUpperCase() + nameFromEmail.slice(1);
    }
    
    return "Current User";
  };
  const [showTestModal, setShowTestModal] = useState(false);
  const [testForm, setTestForm] = useState({
    name: "",
    email: "",
    mobile: "",
    query: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [manualResponseModal, setManualResponseModal] = useState({
    show: false,
    query: null,
    response: "",
    submitting: false,
  });

  // State for view response modal
  const [viewResponseModal, setViewResponseModal] = useState({
    show: false,
    response: '',
    query: null,
  });

  // State for notes modal system
  const [notesModal, setNotesModal] = useState({
    show: false,
    queryId: null,
    notes: [], // Array of note objects
    newNote: "",
    submitting: false,
    editingNoteId: null,
    editingContent: "",
  });

  // Persistent notes cache to prevent loss on modal reopen and page reload
  const [notesCache, setNotesCache] = useState(() => {
    // Initialize from localStorage on component mount
    try {
      const savedCache = localStorage.getItem('supportQueriesNotesCache');
      if (savedCache) {
        const parsedCache = JSON.parse(savedCache);
        return new Map(Object.entries(parsedCache));
      }
    } catch (error) {
      console.error('Failed to load notes cache from localStorage:', error);
    }
    return new Map();
  });

  // Save notes cache to localStorage whenever it changes
  useEffect(() => {
    try {
      const cacheObject = Object.fromEntries(notesCache);
      localStorage.setItem('supportQueriesNotesCache', JSON.stringify(cacheObject));
    } catch (error) {
      console.error('Failed to save notes cache to localStorage:', error);
    }
  }, [notesCache]);

  const [listView, setListView] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

    // Date range filter state for query filtering
    const [filterStartDate, setFilterStartDate] = useState(null);
    const [filterEndDate, setFilterEndDate] = useState(null);

  const usageLimitReached = billingInfo &&
  billingInfo.limits &&
  billingInfo.usage &&
  (billingInfo.usage.tokens >= billingInfo.limits.tokens ||
    billingInfo.usage.emails >= billingInfo.limits.emails);

  // Helper to compute exceeded quotas and messages
  const getExceededDetails = () => {
    // Only show tokens and emails for Support Queries banner
    if (!billingInfo || !billingInfo.limits || !billingInfo.usage) return [];
    const asNumber = (v) => (typeof v === 'number' ? v : v == null ? NaN : Number(v));
    const details = [];
    const tokensUsed = asNumber(billingInfo.usage?.tokens);
    const tokensLimit = asNumber(billingInfo.limits?.tokens);
    if (!Number.isNaN(tokensUsed) && !Number.isNaN(tokensLimit) && tokensUsed >= tokensLimit) {
      details.push({
        key: 'tokens',
        title: 'AI Tokens',
        used: tokensUsed,
        limit: tokensLimit,
        why: "Your AI token quota is exhausted — automated AI responses are paused. Users won't be able to get AI-powered support queries from the widget. Please upgrade to restore this functionality."
      });
    }
    const emailsUsed = asNumber(billingInfo.usage?.emails);
    const emailsLimit = asNumber(billingInfo.limits?.emails);
    if (!Number.isNaN(emailsUsed) && !Number.isNaN(emailsLimit) && emailsUsed >= emailsLimit) {
      details.push({
        key: 'emails',
        title: 'Emails',
        used: emailsUsed,
        limit: emailsLimit,
        why: "Your monthly email quota is exhausted — support notification emails can't be sent. Please upgrade to restore email delivery."
      });
    }
    return details;
  };

  // Bulk selection state
  const [selectedQueries, setSelectedQueries] = useState(new Set());
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  useEffect(() => {
    loadQueries();
    fetchBillingInfo();
    // Clean up old cache entries (optional - keeps localStorage manageable)
    cleanupNotesCache();
  }, [fetchBillingInfo]);

  

  // Clean up notes cache - remove entries older than 7 days
  const cleanupNotesCache = () => {
    try {
      const savedCache = localStorage.getItem('supportQueriesNotesCache');
      if (savedCache) {
        const parsedCache = JSON.parse(savedCache);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        const cleanedCache = {};
        Object.entries(parsedCache).forEach(([queryId, notes]) => {
          if (Array.isArray(notes) && notes.length > 0) {
            // Keep cache entries that have notes added within last 7 days
            const hasRecentNotes = notes.some(note => {
              const noteDate = new Date(note.added_at || note.updated_at);
              return noteDate > sevenDaysAgo;
            });
            if (hasRecentNotes) {
              cleanedCache[queryId] = notes;
            }
          }
        });
        
        localStorage.setItem('supportQueriesNotesCache', JSON.stringify(cleanedCache));
        setNotesCache(new Map(Object.entries(cleanedCache)));
      }
    } catch (error) {
      console.error('Failed to cleanup notes cache:', error);
    }
  };

  const loadQueries = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getSupportQueries();
      console.log("Loaded queries data:", data.queries); // Debug log to see query structure
      setQueries(data.queries || []);
    } catch (error) {
      console.error("Failed to load support queries:", error);
      toast.error("Failed to load support queries");
    } finally {
      setLoading(false);
    }
  };



  // changes for download CSV
  const statusOptions = ["pending", "responded", "closed"];
  const [showDownloadCSVModal, setShowDownloadCSVModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  const selectAll = () => setSelectedStatuses([...statusOptions]);
  const clearAll = () => setSelectedStatuses([]);

  const handleCheckboxToggle = (status) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleDownloadCSVSubmit = async (e) => {
    e.preventDefault();

    if (selectedStatuses.length === 0) {
      toast.error("Please apply the filters");
      return;
    }

    setSubmitting(true);

    try {
      const res = await ApiService.downloadCSVforSupportQueries(selectedStatuses, startDate, endDate);
      const recordsFound = res.headers["x-records-found"] || '0';

      if (recordsFound === '0') {
        toast.error("No matching records found!!");
        return;
      }
      const filename = res.headers["x-file-name"] || "support_queries.csv";
      console.log(filename);
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      clearAll();
      setEndDate("");
      setStartDate("");
      setShowDownloadCSVModal(false);
      toast.success('File downloaded successfuly');
    }

    catch (error) {
      console.error("Test submission error:", error);
      toast.error("Failed to Download");
    }

    finally {
      setSubmitting(false);
    }
  }



  const handleTestSubmit = async (e) => {
    e.preventDefault();

    if (
      !testForm.name ||
      !testForm.email ||
      !testForm.mobile ||
      !testForm.query
    ) {
      toast.error("Please fill in all fields");
      return;
    }

    // Check usage before submitting test query
    if (billingInfo && billingInfo.usage && billingInfo.limits && billingInfo.usage.emails >= billingInfo.limits.emails) {
      toast.error("You have reached your email limit. Please upgrade your plan.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await ApiService.submitSupportQuery(
        testForm.email,
        testForm.query,
        testForm.name,
        testForm.mobile
      );

      if (result.success) {
        toast.success("Test query submitted successfully!");
        setShowTestModal(false);
        setTestForm({ name: "", email: "", mobile: "", query: "" });
        loadQueries();
      } else {
        toast.error("Failed to submit test query");
      }
    } catch (error) {
      console.error("Test submission error:", error);
      toast.error("Failed to submit test query");
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualResponse = (query) => {
    // Prevent opening modal for closed queries
    if (query.status === 'closed') {
      toast.error("Cannot send manual response for closed queries");
      return;
    }

    setManualResponseModal({
      show: true,
      query: query,
      response: query.ai_response || "",
      submitting: false,
    });
  };

  const submitManualResponse = async () => {
    if (!manualResponseModal.response.trim()) {
      toast.error("Please enter a response");
      return;
    }

    // Check usage before sending manual response
    if (billingInfo && billingInfo.usage && billingInfo.limits && billingInfo.usage.emails >= billingInfo.limits.emails) {
      toast.error("You have reached your email limit. Please upgrade your plan.");
      return;
    }

    setManualResponseModal((prev) => ({ ...prev, submitting: true }));  

    try {
      const result = await ApiService.submitManualResponse(
        manualResponseModal.query.query_id,
        manualResponseModal.query.customer_email,
        manualResponseModal.response
      );

      if (result.success) {
        toast.success("Manual response sent successfully!");
        setManualResponseModal({
          show: false,
          query: null,
          response: "",
          submitting: false,
        });
        loadQueries();
      } else {
        toast.error("Failed to send manual response");
      }
    } catch (error) {
      console.error("Manual response error:", error);
      toast.error("Failed to send manual response");
    } finally {
      setManualResponseModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const handleCloseQuery = async (queryId) => {
    try {
      const result = await ApiService.closeSupportQuery([queryId], "closed");
      if (result.success) {
        toast.success("Query closed successfully!");
        loadQueries(); // Reload queries to update the status
      } else {
        toast.error("Failed to close query");
      }
    } catch (error) {
      console.error("Close query error:", error);
      toast.error(error.message || "Failed to close query");
    }
  };

  // Bulk selection functions
  const handleSelectQuery = (queryId) => {
    const newSelected = new Set(selectedQueries);
    if (newSelected.has(queryId)) {
      newSelected.delete(queryId);
    } else {
      newSelected.add(queryId);
    }
    setSelectedQueries(newSelected);
    
    // Update select all checkbox state
    const closableQueries = currentQueries.filter(q => q.status !== 'closed');
    setIsSelectAllChecked(closableQueries.every(q => newSelected.has(q.query_id)));
  };

  const handleSelectAll = () => {
    const closableQueries = currentQueries.filter(q => q.status !== 'closed');
    if (isSelectAllChecked) {
      // Deselect all current page queries
      const newSelected = new Set(selectedQueries);
      closableQueries.forEach(q => newSelected.delete(q.query_id));
      setSelectedQueries(newSelected);
      setIsSelectAllChecked(false);
    } else {
      // Select all current page queries
      const newSelected = new Set(selectedQueries);
      closableQueries.forEach(q => newSelected.add(q.query_id));
      setSelectedQueries(newSelected);
      setIsSelectAllChecked(true);
    }
  };

  const handleBulkClose = async () => {
    if (selectedQueries.size === 0) {
      toast.error("Please select queries to close");
      return;
    }

    if (!confirm(`Are you sure you want to close ${selectedQueries.size} selected queries?`)) {
      return;
    }

    try {
      const queryIds = Array.from(selectedQueries);
      const result = await ApiService.closeSupportQuery(queryIds, "closed");
      const successCount = result.modified_count;

      if (successCount > 0) {
        toast.success(`Successfully closed ${successCount} queries`);
      }

      // Clear selection and reload queries
      setSelectedQueries(new Set());
      setIsSelectAllChecked(false);
      loadQueries();
    } catch (error) {
      console.error("Bulk close error:", error);
      toast.error("Failed to close selected queries");
    }
  };

  // Notes management functions
  const handleAddNote = async () => {
    if (!notesModal.newNote.trim()) {
      toast.error("Please enter a note");
      return;
    }

    try {
      setNotesModal((prev) => ({ ...prev, submitting: true }));
      
      const result = await ApiService.addNoteToSupportQuery(notesModal.queryId, notesModal.newNote);
      
      if (result.success) {
        toast.success("Note added successfully!");
        
        // Create new note object with unique ID
        const newNoteObj = {
          id: `temp_${Date.now()}_${Math.random()}`, // More unique temporary ID
          note: notesModal.newNote,
          added_by: getCurrentUserName(), // Use current Clerk user
          added_at: new Date().toISOString(),
        };
        
        // Get existing notes from cache for this query
        const existingCachedNotes = notesCache.get(notesModal.queryId) || [];
        const updatedCachedNotes = [...existingCachedNotes, newNoteObj];
        
        // Update persistent cache
        setNotesCache(prevCache => {
          const newCache = new Map(prevCache);
          newCache.set(notesModal.queryId, updatedCachedNotes);
          return newCache;
        });
        
        // Add new note to modal state immediately
        setNotesModal((prev) => ({
          ...prev,
          notes: [...prev.notes, newNoteObj],
          newNote: "",
          submitting: false,
        }));
        
        // Update the local queries state to persist notes across modal openings
        setQueries(prevQueries => 
          prevQueries.map(query => {
            if (query.query_id === notesModal.queryId) {
              const updatedNotes = query.notes ? 
                (Array.isArray(query.notes) ? [...query.notes, newNoteObj] : [query.notes, newNoteObj]) :
                [newNoteObj];
              return {
                ...query,
                notes: updatedNotes
              };
            }
            return query;
          })
        );
        
        // Reload queries in background without affecting current modal state
        loadQueries().catch(error => {
          console.error("Failed to reload queries after adding note:", error);
        });
        
      } else {
        toast.error("Failed to add note");
      }
    } catch (error) {
      console.error("Add note error:", error);
      toast.error(error.message || "Failed to add note");
    } finally {
      setNotesModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const handleEditNote = (noteId, currentContent) => {
    setNotesModal(prev => ({
      ...prev,
      editingNoteId: noteId,
      editingContent: currentContent
    }));
  };

  const handleSaveEditNote = async (noteId) => {
    if (!notesModal.editingContent.trim()) {
      toast.error("Note content cannot be empty");
      return;
    }

    try {
      setNotesModal((prev) => ({ ...prev, submitting: true }));
      
      // Here you would call an API to update the specific note
      // For now, we'll update it locally
      const updatedNotes = notesModal.notes.map(note => 
        note.id === noteId 
          ? { ...note, note: notesModal.editingContent, updated_at: new Date().toISOString() }
          : note
      );

      // Update cached notes
      setNotesCache(prevCache => {
        const newCache = new Map(prevCache);
        newCache.set(notesModal.queryId, updatedNotes);
        return newCache;
      });

      setNotesModal((prev) => ({
        ...prev,
        notes: updatedNotes,
        editingNoteId: null,
        editingContent: "",
        submitting: false,
      }));

      toast.success("Note updated successfully!");
    } catch (error) {
      console.error("Update note error:", error);
      toast.error(error.message || "Failed to update note");
    } finally {
      setNotesModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) {
      return;
    }

    try {
      setNotesModal((prev) => ({ ...prev, submitting: true }));
      
      // Here you would call an API to delete the specific note
      // For now, we'll remove it locally
      const updatedNotes = notesModal.notes.filter(note => note.id !== noteId);

      // Update cached notes
      setNotesCache(prevCache => {
        const newCache = new Map(prevCache);
        newCache.set(notesModal.queryId, updatedNotes);
        return newCache;
      });

      setNotesModal((prev) => ({
        ...prev,
        notes: updatedNotes,
        submitting: false,
      }));

      toast.success("Note deleted successfully!");
    } catch (error) {
      console.error("Delete note error:", error);
      toast.error(error.message || "Failed to delete note");
    } finally {
      setNotesModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  const handleCancelEdit = () => {
    setNotesModal(prev => ({
      ...prev,
      editingNoteId: null,
      editingContent: ""
    }));
  };

  const openNotesModal = async (queryId) => {
    try {
      console.log(`Opening notes modal for query ${queryId}`);
      console.log('Current notes cache:', Object.fromEntries(notesCache));
      
      // First priority: Check cached notes for this query
      let existingNotes = [];
      const cachedNotes = notesCache.get(queryId);
      
      if (cachedNotes && cachedNotes.length > 0) {
        console.log(`Found ${cachedNotes.length} cached notes for query ${queryId}:`, cachedNotes);
        existingNotes = cachedNotes;
      } else {
        console.log(`No cached notes found for query ${queryId}, checking other sources...`);
        // Second priority: Check if modal already has notes for this query
        if (notesModal.queryId === queryId && notesModal.notes.length > 0) {
          console.log(`Using existing modal notes for query ${queryId}: ${notesModal.notes.length} notes`);
          existingNotes = notesModal.notes;
          // Cache these notes for future use
          setNotesCache(prevCache => {
            const newCache = new Map(prevCache);
            newCache.set(queryId, notesModal.notes);
            return newCache;
          });
        } else {
          // Third priority: Extract from local queries state
          const query = queries.find(q => q.query_id === queryId);
          console.log(`Query data for ${queryId}:`, query);
          
          // Helper function to process a single note
          const processNote = (noteData, index = 0) => {
            if (!noteData) return null;
            
            if (typeof noteData === 'string') {
              return {
                id: `note_${queryId}_${index}_${Date.now()}`,
                note: noteData,
                added_by: getCurrentUserName(), // Use current Clerk user instead of "Unknown User"
                added_at: new Date().toISOString()
              };
            } else if (typeof noteData === 'object' && noteData.note) {
              return {
                id: noteData.id || `note_${queryId}_${index}_${Date.now()}`,
                note: noteData.note,
                added_by: noteData.added_by || getCurrentUserName(), // Use current Clerk user as fallback
                added_at: noteData.added_at || new Date().toISOString(),
                updated_at: noteData.updated_at
              };
            }
            return null;
          };
          
          // Process notes from different possible fields
          if (query?.notes) {
            if (Array.isArray(query.notes)) {
              // Handle array of notes
              existingNotes = query.notes
                .map((note, index) => processNote(note, index))
                .filter(note => note !== null);
            } else {
              // Handle single note object or string
              const processedNote = processNote(query.notes, 0);
              if (processedNote) existingNotes = [processedNote];
            }
          } else if (query?.note) {
            // Handle legacy single note field
            const processedNote = processNote(query.note, 0);
            if (processedNote) existingNotes = [processedNote];
          }

          // Cache the processed notes
          if (existingNotes.length > 0) {
            console.log(`Caching ${existingNotes.length} processed notes for query ${queryId}`);
            setNotesCache(prevCache => {
              const newCache = new Map(prevCache);
              newCache.set(queryId, existingNotes);
              return newCache;
            });
          }

          // Last resort: Fetch from server only if no notes found anywhere
          if (existingNotes.length === 0) {
            try {
              console.log("No notes found locally, fetching fresh data from server...");
              const updatedQueries = await ApiService.getSupportQueries();
              const updatedQuery = updatedQueries.queries?.find(q => q.query_id === queryId);
              
              if (updatedQuery?.notes) {
                if (Array.isArray(updatedQuery.notes)) {
                  existingNotes = updatedQuery.notes
                    .map((note, index) => processNote(note, index))
                    .filter(note => note !== null);
                } else {
                  const processedNote = processNote(updatedQuery.notes, 0);
                  if (processedNote) existingNotes = [processedNote];
                }
                
                // Cache the server notes
                if (existingNotes.length > 0) {
                  console.log(`Caching ${existingNotes.length} server notes for query ${queryId}`);
                  setNotesCache(prevCache => {
                    const newCache = new Map(prevCache);
                    newCache.set(queryId, existingNotes);
                    return newCache;
                  });
                }
              }
              
              // Update local queries state with fresh data
              if (updatedQueries.queries) {
                setQueries(updatedQueries.queries);
              }
            } catch (error) {
              console.error("Failed to refresh query data:", error);
            }
          }
        }
      }

      console.log(`Final notes count for query ${queryId}: ${existingNotes.length} notes`, existingNotes);

      setNotesModal({
        show: true,
        queryId: queryId,
        notes: existingNotes,
        newNote: "",
        submitting: false,
        editingNoteId: null,
        editingContent: "",
      });
    } catch (error) {
      console.error("Error opening notes modal:", error);
      setNotesModal({
        show: true,
        queryId: queryId,
        notes: [],
        newNote: "",
        submitting: false,
        editingNoteId: null,
        editingContent: "",
      });
    }
  };

  // const shouldShowManualButton = (query) => {
  //   // Show manual button if:
  //   // 1. No AI response was generated OR
  //   // 2. need_to_escalate is true
  //   return !query.ai_response || query.need_to_escalate === true;
  // };
  const getStatusIcon = (status) => {
    switch (status) {
      case "responded":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "pending":
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case "closed":
        return <XCircle className="h-5 w-5 text-gray-500" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "responded":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };
  // Memoize status counts based on current search and date filters (excluding status filter)
  const statusCounts = useMemo(() => {
    const counts = { total: 0, responded: 0, pending: 0, closed: 0 };
    
    // Filter queries by search and date only (not by status)
    const queriesFilteredBySearchAndDate = queries.filter((query) => {
      const matchesSearch =
        query.query.toLowerCase().includes(searchQuery.toLowerCase()) ||
        query.customer_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (query.customer_name &&
          query.customer_name
            .toLowerCase()
            .includes(searchQuery.toLowerCase())) ||
        (query.ticket_id && query.ticket_id.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
        (query.mobile && query.mobile.includes(searchQuery));
      
      let matchesDate = true;
      if (filterStartDate || filterEndDate) {
        // Assume query.created_at is ISO string
        const created = query.created_at ? dayjs(query.created_at) : null;
        if (filterStartDate && created) {
          matchesDate = matchesDate && created.isAfter(dayjs(filterStartDate).subtract(1, 'day'));
        }
        if (filterEndDate && created) {
          matchesDate = matchesDate && created.isBefore(dayjs(filterEndDate).add(1, 'day'));
        }
      }
      return matchesSearch && matchesDate;
    });
    
    counts.total = queriesFilteredBySearchAndDate.length;
    for (const q of queriesFilteredBySearchAndDate) {
      if (q && q.status) {
        if (q.status === 'responded') counts.responded += 1;
        else if (q.status === 'pending') counts.pending += 1;
        else if (q.status === 'closed') counts.closed += 1;
      }
    }
    return counts;
  }, [queries, searchQuery, filterStartDate, filterEndDate]);

  // Reusable stats card component
  const StatsCard = ({ title, count, Icon, statusKey, colors = 'from-gray-400 to-gray-600' }) => {
    const isActive = statusFilter === statusKey || (statusKey === 'all' && statusFilter === 'all');
    return (
      <button
        type="button"
        onClick={() => setStatusFilter(statusKey)}
        className={`group text-left w-full bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 hover:shadow-xl hover:border-gray-300/50 transition-all duration-300 hover:-translate-y-1 focus:outline-none ${isActive ? 'ring-2 ring-blue-300' : ''}`}
      >
        <div className="p-6">
          <div className="flex items-center">
            <div className={`p-3 bg-gradient-to-br ${colors} rounded-xl mr-4 group-hover:scale-110 transition-transform duration-200`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-2xl font-bold text-gray-900">{count}</div>
              <div className="text-sm font-medium text-gray-600">{title}</div>
            </div>
          </div>
        </div>
      </button>
    );
  };
  const filteredQueries = queries.filter((query) => {
    const matchesSearch =
      query.query.toLowerCase().includes(searchQuery.toLowerCase()) ||
      query.customer_email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (query.customer_name &&
        query.customer_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase())) ||
      (query.ticket_id && query.ticket_id.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
      (query.mobile && query.mobile.includes(searchQuery));
    const matchesStatus =
      statusFilter === "all" || query.status === statusFilter;
    let matchesDate = true;
    if (filterStartDate || filterEndDate) {
      // Assume query.created_at is ISO string
      const created = query.created_at ? dayjs(query.created_at) : null;
      if (filterStartDate && created) {
        matchesDate = matchesDate && created.isAfter(dayjs(filterStartDate).subtract(1, 'day'));
      }
      if (filterEndDate && created) {
        matchesDate = matchesDate && created.isBefore(dayjs(filterEndDate).add(1, 'day'));
      }
    }
    return matchesSearch && matchesStatus && matchesDate;
  });

  // Pagination calculations
  const totalItems = filteredQueries.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentQueries = filteredQueries.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, filterStartDate, filterEndDate]);

  // Reset selections when filters or pagination change
  useEffect(() => {
    setSelectedQueries(new Set());
    setIsSelectAllChecked(false);
  }, [searchQuery, statusFilter, filterStartDate, filterEndDate, currentPage]);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Support Queries
          </h2>
          <p className="text-gray-600 mt-2">
            Manage customer support queries with AI-powered responses
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {/* Date Range Filter */}
          <div className="relative flex flex-row gap-2 items-stretch min-w-[100px] z-50">
            <DateRangePicker
              startDate={filterStartDate ? filterStartDate.toDate ? filterStartDate.toDate() : filterStartDate : null}
              endDate={filterEndDate ? filterEndDate.toDate ? filterEndDate.toDate() : filterEndDate : null}
              onChange={(since, until) => {
                // Keep internal state as Dayjs or Date consistently.
                // Elsewhere we use dayjs(createdAt) comparisons, so store Dayjs objects
                // when available; fall back to Date.
                setFilterStartDate(since ? dayjs(since) : null);
                setFilterEndDate(until ? dayjs(until) : null);
              }}
              className="w-full"
            />
          </div>
          
          <button
            onClick={() => setShowDownloadCSVModal(true)}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Download CSV
          </button>
          <button
            onClick={() => setShowTestModal(true)}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Test Query
          </button>
        </div>
      </div>
      {(() => {
        const exceeded = getExceededDetails();
        if (!exceeded || exceeded.length === 0) return null;
        return (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50/60 backdrop-blur p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className="h-9 w-9 rounded-lg bg-red-100 flex items-center justify-center border border-red-200">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-red-900">
                      Usage limit reached
                    </h4>
                    <p className="text-sm text-red-700/90 mt-0.5">
                      You've reached the following monthly quota{exceeded.length>1?'s':''}. Upgrade to increase limits and re-enable affected features.
                    </p>
                  </div>
                  <a
                    href="/billing"
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 text-white px-3 py-2 text-sm font-semibold shadow-sm hover:bg-red-700 transition-colors"
                  >
                    View billing & upgrade
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {exceeded.map((d) => {
                    const percent = d.limit > 0 ? Math.min(100, Math.round((d.used / d.limit) * 100)) : 100;
                    return (
                      <div key={d.key} className="rounded-lg border border-red-200 bg-white/60 p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium uppercase tracking-wide text-red-700/80">
                            {d.title}
                          </span>
                          <span className="text-[11px] font-semibold text-red-700 bg-red-100 border border-red-200 rounded-full px-2 py-0.5">
                            {d.used} / {d.limit}
                          </span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-red-100 border border-red-200 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-red-500 to-red-600"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <p className="mt-2 text-xs text-red-700/90 leading-relaxed">
                          {d.why}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()} 
      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatsCard title="Total Queries" count={statusCounts.total} Icon={Mail} statusKey="all" colors="from-blue-500 to-indigo-600" />
        <StatsCard title="Responded" count={statusCounts.responded} Icon={CheckCircle} statusKey="responded" colors="from-green-500 to-emerald-600" />
        <StatsCard title="Pending" count={statusCounts.pending} Icon={Clock} statusKey="pending" colors="from-yellow-500 to-orange-600" />
        <StatsCard title="Closed" count={statusCounts.closed} Icon={XCircle} statusKey="closed" colors="from-gray-500 to-slate-600" />
      </div>

      {/* Filters - Improved Full-Width Layout */}
<div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 relative z-40">
  <div className="p-4">
    <div className="flex flex-wrap md:flex-nowrap items-stretch gap-3 w-full">
      {/* Search Input */}
      <div className="relative flex-1 min-w-[200px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name, email, mobile, ticket id, or query..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full h-14 pl-10 pr-3 border border-gray-300 rounded-lg shadow-sm text-md focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
        />
      </div>

      {/* Status Filter with Clear Button */}
      <div className="relative flex items-stretch gap-2 min-w-[250px] max-w-[400px]">
        <div className="relative flex-1 min-w-[160px]">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full h-14 pl-10 pr-8 border border-gray-300 rounded-lg shadow-sm text-md appearance-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="responded">Responded</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <button
          onClick={() => setStatusFilter("all")}
          className="h-14 px-6 bg-white border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-200 hover:border-gray-400 transition-all duration-200 text-sm font-medium flex items-center justify-center min-w-[80px]"
          title="Clear status filter"
        >
          Clear
        </button>
      </div>

      {/* View Toggle */}
      <button
        type="button"
        onClick={() => setListView((prev) => !prev)}
        className={`h-14 px-6 rounded-lg border border-gray-300 flex items-center justify-center shadow-sm transition-all duration-200 min-w-[120px] ${
          listView
            ? 'bg-blue-600 text-white'
            : 'bg-white text-gray-700 hover:bg-gray-100'
        }`}
        title={listView ? 'Switch to Card View' : 'Switch to List View'}
      >
        <span className="text-sm font-medium">
          {listView ? 'Card View' : 'List View'}
        </span>
      </button>

    </div>
  </div>
</div>


      {/* Queries List */}
      {filteredQueries.length === 0 ? (
        <div className="text-center py-16">
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl"></div>
            <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center">
              <Mail className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {queries.length === 0
              ? "No support queries yet"
              : "No queries match your filters"}
          </h3>
          <p className="text-gray-500 mb-8 max-w-md mx-auto">
            {queries.length === 0
              ? "Customer queries will appear here when submitted through your widgets"
              : "Try adjusting your search or filter settings"}
          </p>
        </div>
      ) : (
        <>
          {/* Bulk Actions Bar */}
          {selectedQueries.size > 0 && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-blue-900">
                    {selectedQueries.size} query{selectedQueries.size !== 1 ? 'ies' : ''} selected
                  </span>
                  <button
                    onClick={() => {
                      setSelectedQueries(new Set());
                      setIsSelectAllChecked(false);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Clear selection
                  </button>
                </div>
                <button
                  onClick={handleBulkClose}
                  className="flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Close Selected
                </button>
              </div>
            </div>
          )}
          
          {listView ? (
          // List View Table
          <div className="overflow-x-auto w-full ">
            <table className="min-w-full bg-white rounded-xl shadow-md text-xs md:text-sm">
              <thead>
                <tr>
                  <th className="px-2 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={isSelectAllChecked}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-gray-700">Status</th>
                  <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Date</th>
                  <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Name</th>
                  <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Email</th>
                  <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Mobile</th>
                  <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Query</th>
                  <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Reply from AI Support Agent</th>
                  <th className="px-2 md:px-4 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentQueries.map((query) => (
                  <tr key={query.query_id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-2 py-2 align-top text-center">
                      <input
                        type="checkbox"
                        checked={selectedQueries.has(query.query_id)}
                        onChange={() => handleSelectQuery(query.query_id)}
                        disabled={query.status === 'closed'}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                      />
                    </td>
                    <td className="px-2 md:px-4 py-2 align-top text-center min-w-[90px] font-semibold">
                      <div className="flex flex-col items-center gap-1 text-center">
                        <span className={`inline-flex items-center justify-center px-2 py-1 min-w-[80px] h-7 rounded-full text-xs font-medium shadow-sm text-center ${getStatusColor(query.status)}`}>{query.status.toLowerCase()}</span>
                        {(query.status === 'pending' || query.status === 'responded' || query.status === 'closed') && (
                          <button
                            className="flex items-center justify-center px-2 py-1 min-w-[80px] h-7 rounded-full text-xs font-medium bg-transparent text-gray-700 hover:bg-gray-300 transition text-center"
                            title="View Response"
                            onClick={() => setViewResponseModal({
                              show: true,
                              response: query.ai_response || query.response || (query.human_response && query.human_response.join('\n\n')) || '',
                              query,
                            })}
                          >
                            <Eye className="h-4 w-4 mr-1" /> View
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-2 md:px-4 py-2 align-top text-gray-600 min-w-[110px] whitespace-nowrap ">
                      {new Date(query.created_at).toLocaleDateString()}<br />
                      {new Date(query.created_at).toLocaleTimeString()}
                    </td>
                    <td className="px-2 md:px-4 py-2 align-top text-gray-900 min-w-[100px] whitespace-nowrap font-medium">{query.name || 'N/A'}</td>
                    <td className="px-2 md:px-4 py-2 align-top break-all text-gary-600 min-w-[120px] ">{query.customer_email}</td>
                    <td className="px-2 md:px-4 py-2 align-top text-gray-600 min-w-[90px] whitespace-nowrap ">{query.mobile || 'N/A'}</td>
                    <td className="px-2 md:px-4 py-2 align-top text-gray-600 max-w-[120px] truncate min-w-[120px] " title={query.query}>{query.query}</td>
                    <td className="px-2 md:px-4 py-2 align-top text-gray-600 max-w-[120px] truncate min-w-[120px] " title={query.ai_response || query.response}>
                      {(query.ai_response || query.response) ? (query.ai_response || query.response) : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-2 md:px-6 py-2 md:py-4 align-top min-w-[90px] md:min-w-[120px] font-semibold">
                      <div className="flex flex-col space-y-1 md:justify-center md:items-center">
                        {query.status === 'closed' ? (
                          <>
                            <button
                              className="w-full min-w-[100px] max-w-[120px] h-10 px-2 py-2 text-xs md:text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 truncate"
                              disabled
                              title="Query Closed"
                            >
                              <span className="hidden md:inline">Closed</span>
                              <span className="md:hidden">Closed</span>
                            </button>
                            <button
                              onClick={() => openNotesModal(query.query_id)}
                              className="w-full min-w-[100px] max-w-[120px] h-10 px-2 py-2 text-xs md:text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 truncate"
                              title="Manage Notes"
                            >
                              <span className="hidden md:inline">Manage Notes</span>
                              <span className="md:hidden">Notes</span>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleManualResponse(query)}
                              className="w-full min-w-[100px] max-w-[120px] h-10 px-2 py-2 text-xs md:text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 truncate"
                              title="Edit Response"
                            >
                              <span className="hidden md:inline">Edit Response</span>
                              <span className="md:hidden">Edit</span>
                            </button>
                            <button
                              onClick={() => openNotesModal(query.query_id)}
                              className="w-full min-w-[100px] max-w-[120px] h-10 px-2 py-2 text-xs md:text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 truncate"
                              title="Manage Notes"
                            >
                              <span className="hidden md:inline">Manage Notes</span>
                              <span className="md:hidden">Notes</span>
                            </button>
                            <button
                              onClick={() => handleCloseQuery(query.query_id)}
                              className="w-full min-w-[100px] max-w-[120px] h-10 px-2 py-2 text-xs md:text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 truncate"
                              title="Close Query"
                            >
                              <span className="hidden md:inline">Close</span>
                              <span className="md:hidden">Close</span>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="space-y-4">
            {currentQueries.map((query) => (
              <div
                key={query.query_id}
                className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 hover:shadow-xl hover:border-gray-300/50 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={selectedQueries.has(query.query_id)}
                        onChange={() => handleSelectQuery(query.query_id)}
                        disabled={query.status === 'closed'}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                      />
                      <div className="p-2 bg-gray-100 rounded-xl group-hover:bg-gradient-to-br group-hover:from-gray-100 group-hover:to-gray-200 transition-all duration-200">
                        {getStatusIcon(query.status)}
                      </div>
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium shadow-sm ${getStatusColor(
                          query.status
                        )}`}
                      >
                        {query.status}
                      </span>
                      <span className="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full">
                        {new Date(query.created_at).toLocaleDateString()} at{" "}
                        {new Date(query.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>{" "}
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                          <Mail className="h-4 w-4 mr-2 text-blue-500" />
                          Customer Name:
                        </h4>
                        <p className="text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100/50 p-3 rounded-xl border border-gray-200/50">
                          {query.name || "N/A"}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                          <Mail className="h-4 w-4 mr-2 text-green-500" />
                          Mobile Number:
                        </h4>
                        <p className="text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100/50 p-3 rounded-xl border border-gray-200/50">
                          {query.mobile || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <Mail className="h-4 w-4 mr-2 text-blue-500" />
                        Customer Email:
                      </h4>
                      <p className="text-gray-900 bg-gradient-to-r from-gray-50 to-gray-100/50 p-3 rounded-xl border border-gray-200/50">
                        {query.customer_email}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <BarChart3 className="h-4 w-4 mr-2 text-indigo-500" />
                        Query:
                      </h4>
                      <p className="text-gray-900 bg-gradient-to-r from-blue-50 to-indigo-50/50 p-4 rounded-xl border border-blue-200/50 shadow-sm">
                        {query.query}
                      </p>
                    </div>{" "}
                    {(query.response || query.ai_response) && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                          <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                          Reply from AI Support Agent:
                        </h4>
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50/50 p-4 rounded-xl border border-green-200/50 shadow-sm">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              h1: ({ node, ...props }) => (
                                <h1
                                  className="text-2xl font-bold text-gray-900 mb-4 mt-6 border-b border-gray-200 pb-2"
                                  {...props}
                                />
                              ),
                              h2: ({ node, ...props }) => (
                                <h2
                                  className="text-xl font-semibold text-gray-800 mb-3 mt-5"
                                  {...props}
                                />
                              ),
                              h3: ({ node, ...props }) => (
                                <h3
                                  className="text-lg font-medium text-gray-800 mb-2 mt-4"
                                  {...props}
                                />
                              ),
                              p: ({ node, ...props }) => (
                                <p
                                  className="text-gray-900 leading-relaxed mb-3"
                                  {...props}
                                />
                              ),
                            ul: ({node, ...props}) => <ul style={{marginLeft: 24, marginBottom: 8, listStyleType: 'disc'}} {...props} />,
                            ol: ({node, ...props}) => <ol style={{marginLeft: 24, marginBottom: 8, listStyleType: 'decimal'}} {...props} />,
                            li: ({node, ...props}) => <li style={{marginBottom: 4}} {...props} />,
                              a: ({ node, ...props }) => (
                                <a
                                  className="text-blue-600 hover:text-blue-800 underline decoration-blue-300 hover:decoration-blue-500 transition-colors"
                                  {...props}
                                />
                              ),
                              code: ({ node, inline, ...props }) =>
                                inline ? (
                                  <code
                                    className="bg-gray-200 text-gray-800 px-1.5 py-0.5 rounded font-mono text-sm"
                                    {...props}
                                  />
                                ) : (
                                  <code
                                    className="block bg-gray-800 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-x-auto"
                                    {...props}
                                  />
                                ),
                              pre: ({ node, ...props }) => (
                                <pre
                                  className="bg-gray-800 rounded-lg mb-4 overflow-hidden"
                                  {...props}
                                />
                              ),
                              blockquote: ({ node, ...props }) => (
                                <blockquote
                                  className="border-l-4 border-blue-300 pl-4 italic text-gray-700 bg-blue-50 py-2 rounded-r-lg mb-4"
                                  {...props}
                                />
                              ),
                            }}
                          >
                            {query?.ai_response || query?.response}
                          </ReactMarkdown>
                        </div>
                      </div>
                    )}{" "}
                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-200/50">
                      {/* Manual Response Button - Disabled if query is closed */}
                      <button
                        onClick={() => handleManualResponse(query)}
                        disabled={query.status === 'closed'}
                        className={`inline-flex items-center justify-center px-3 sm:px-4 py-2 font-medium rounded-xl shadow-lg transition-all duration-200 transform text-sm min-w-0 flex-shrink-0 ${query.status === 'closed'
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-60'
                          : 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:shadow-xl hover:from-orange-600 hover:to-red-700 hover:-translate-y-0.5'
                          }`}
                      >
                        <Edit className="h-4 w-4 mr-1 sm:mr-2 flex-shrink-0" />
                        <span className="hidden md:inline truncate">
                          {query.status === 'closed'
                            ? 'Query Closed'
                            : query.ai_response
                              ? "Edit & Send Response"
                              : "Send Response"}
                        </span>
                        <span className="hidden sm:inline md:hidden truncate">
                          {query.status === 'closed'
                            ? 'Closed'
                            : query.ai_response
                              ? "Edit Response"
                              : "Send Response"}
                        </span>
                        <span className="sm:hidden truncate">
                          {query.status === 'closed'
                            ? 'Closed'
                            : 'Edit'}
                        </span>
                      </button>

                      {/* Manage Notes Button */}
                      <button
                        onClick={() => openNotesModal(query.query_id)}
                        className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 transform hover:-translate-y-0.5 text-sm"
                        title="Manage Notes"
                      >
                        <PlusCircle className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">Manage Notes</span>
                        <span className="sm:hidden">Notes</span>
                      </button>

                      {/* Close Query Button - Only show if not already closed */}
                      {query.status !== 'closed' && (
                        <button
                          onClick={() => handleCloseQuery(query.query_id)}
                          className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-gray-600 hover:to-gray-700 transition-all duration-200 transform hover:-translate-y-0.5 text-sm"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          <span className="hidden sm:inline">Close Query</span>
                          <span className="sm:hidden">Close</span>
                        </button>
                      )}
                    </div>
                    {/* Display Human Responses if any */}
                    {query.human_response && query.human_response.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                          <Send className="h-4 w-4 mr-2 text-purple-500" />
                          Manual Responses:
                        </h4>
                        <div className="space-y-2">
                          {query.human_response.map((response, index) => (
                            <div
                              key={index}
                              className="bg-gradient-to-r from-purple-50 to-indigo-50/50 p-4 rounded-xl border border-purple-200/50 shadow-sm"
                            >
                              <p className="text-gray-900 leading-relaxed">
                                {response}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
      )}{" "}

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between px-4">
          <div className="flex items-center text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-2" />
            <span>Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Prev
            </button>

            {/* Page Numbers */}
            <div className="flex items-center space-x-1">
              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                const isActive = page === currentPage;

                // Show first page, last page, current page, and pages around current
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${isActive
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                        }`}
                    >
                      {page}
                    </button>
                  );
                } else if (
                  (page === currentPage - 2 && currentPage > 3) ||
                  (page === currentPage + 2 && currentPage < totalPages - 2)
                ) {
                  return (
                    <span key={page} className="px-2 text-gray-400">
                      ...
                    </span>
                  );
                }
                return null;
              })}
            </div>

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}

      {/*Download CSV Modal*/}
      {showDownloadCSVModal && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="fixed inset-0 flex items-center justify-center p-5">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md transform transition-all duration-300 scale-100">
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Apply filters
                  </h3>
                  <button
                    onClick={() => {
                      setShowDownloadCSVModal(false);
                      clearAll();
                      setEndDate("");
                      setStartDate("");
                    }}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleDownloadCSVSubmit} className="space-y-5">
                  {/* Checkbox UI */}
                  <div className="p-4 border rounded-xl w-full max-w-sm bg-white shadow-sm">
                    <p className="mb-2 font-semibold">Filter by Status:</p>

                    <div className="flex items-center space-x-4 mb-3">
                      <button
                        type="button"
                        onClick={selectAll}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={clearAll}
                        className="text-sm text-red-500 hover:underline"
                      >
                        Clear All
                      </button>
                    </div>

                    {/* Checkbox elements */}
                    <div className="space-y-2">
                      {statusOptions.map((status) => (
                        <label key={status} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            value={status}
                            checked={selectedStatuses.includes(status)}
                            onChange={() => handleCheckboxToggle(status)}
                            className="accent-blue-500"
                          />
                          <span className="capitalize">{status}</span>
                        </label>
                      ))}
                    </div>
                  </div>


                  {/* Date Range Filter UI */}
                  <div className="p-4 border rounded-xl w-full max-w-sm bg-white shadow-sm">
                    <p className="mb-2 font-semibold">Filter by Date Range:</p>

                    <div className="flex flex-col space-y-4">
                      <div className="flex flex-col">
                        <label htmlFor="start_date" className="text-sm font-medium text-gray-700">Start Date</label>
                        <input
                          type="date"
                          id="start_date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="border rounded-lg p-2 mt-1"
                        />
                      </div>

                      <div className="flex flex-col">
                        <label htmlFor="end_date" className="text-sm font-medium text-gray-700">End Date</label>
                        <input
                          type="date"
                          id="end_date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="border rounded-lg p-2 mt-1"
                        />
                      </div>
                    </div>
                  </div>


                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setShowDownloadCSVModal(false);
                        clearAll();
                        setEndDate("");
                        setStartDate("");
                      }}
                      className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl"
                      disabled={submitting}
                    >
                      {submitting ? "Downloading..." : "Download"}
                    </button>
                  </div>
                </form>

              </div>
            </div>{" "}
          </div>
        </div>
      )
      }{" "}

      {/* Test Query Modal */}
      {showTestModal && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="fixed inset-0 flex items-center justify-center p-5">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md transform transition-all duration-300 scale-100">
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Submit Test Query
                  </h3>
                  <button
                    onClick={() => setShowTestModal(false)}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleTestSubmit} className="space-y-5">
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-blue-500" />
                      Name
                    </label>
                    <input
                      type="text"
                      value={testForm.name}
                      onChange={(e) =>
                        setTestForm({ ...testForm, name: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                      placeholder="Customer Name"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-blue-500" />
                      Customer Email
                    </label>
                    <input
                      type="email"
                      value={testForm.email}
                      onChange={(e) =>
                        setTestForm({ ...testForm, email: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                      placeholder="customer@example.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-blue-500" />
                      Mobile No.
                    </label>
                    <input
                      type="tel"
                      value={testForm.mobile}
                      onChange={(e) =>
                        setTestForm({ ...testForm, mobile: e.target.value })
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                      placeholder="Mobile Number"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <BarChart3 className="h-4 w-4 mr-2 text-indigo-500" />
                      Query
                    </label>
                    <textarea
                      value={testForm.query}
                      onChange={(e) =>
                        setTestForm({ ...testForm, query: e.target.value })
                      }
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 resize-none"
                      placeholder="How do I..."
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setShowTestModal(false)}
                      className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Submit Test Query"}
                    </button>
                  </div>
                </form>
              </div>
            </div>{" "}
          </div>
        </div>
      )}{" "}
      {/* Manual Response Modal */}
      {manualResponseModal.show && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="flex min-h-full items-center justify-center p-8">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-4xl max-h-[90vh] overflow-hidden transform transition-all duration-300 scale-100">
              <div className="flex flex-col h-full max-h-[90vh]">
                {/* Header */}
                <div className="flex-shrink-0 px-8 py-6 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-gray-900">
                      Send Manual Response
                    </h3>
                    <button
                      onClick={() =>
                        setManualResponseModal({
                          show: false,
                          query: null,
                          response: "",
                          submitting: false,
                        })
                      }
                      className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex-shrink-0"
                    >
                      <X className="h-5 w-5" />
                    </button>
  </div>
                </div>
                <div className="flex-1 overflow-y-auto px-8 py-6">
                  <div className="space-y-6">
                    {manualResponseModal.query && (
                      <div className="space-y-4">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50/50 p-5 rounded-xl border border-blue-200/50">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">
                            Customer Query:
                          </h4>
                          <p className="text-gray-900 text-base leading-relaxed break-words">
                            {manualResponseModal.query.query}
                          </p>
                        </div>

                        <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 p-5 rounded-xl border border-gray-200/50">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">
                            Customer Email:
                          </h4>
                          <p className="text-gray-900 text-base break-all">
                            {manualResponseModal.query.customer_email}
                          </p>
                        </div>

                        {manualResponseModal.query.ai_response && (
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50/50 p-5 rounded-xl border border-green-200/50">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">
                              Original Reply from AI Support Agent:
                            </h4>
                            <div className="max-h-48 overflow-y-auto">
                              <p className="text-gray-700 text-sm leading-relaxed break-words">
                                {manualResponseModal.query.ai_response}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
 )}

                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                        <Send className="h-4 w-4 mr-2 text-purple-500" />
                        Your Response:
                      </label>
                      <textarea
                        value={manualResponseModal.response}
                        onChange={(e) =>
                          setManualResponseModal((prev) => ({
                            ...prev,
                            response: e.target.value,
                          }))
                        }
                        rows={8}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500 transition-all duration-200 resize-none text-base leading-relaxed"
                        placeholder="Enter your response to the customer..."
                        disabled={manualResponseModal.submitting}
                      />
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 px-8 py-6 border-t border-gray-200 bg-gray-50/50">
                  <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                    <button
                      type="button"
                      onClick={() =>
                        setManualResponseModal({
                          show: false,
                          query: null,
                          response: "",
                          submitting: false,
                        })
                      }
                      className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                      disabled={manualResponseModal.submitting}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={submitManualResponse}
                      className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      disabled={
                        manualResponseModal.submitting ||
                        !manualResponseModal.response.trim()
                      }
                    >
                      {manualResponseModal.submitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span>Send Response</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Response Modal */}
      {viewResponseModal.show && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="flex min-h-full items-center justify-center p-8">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-hidden transform transition-all duration-300 scale-100">
              <div className="flex flex-col h-full max-h-[90vh]">
                {/* Header */}
                <div className="flex-shrink-0 px-8 py-6 border-b border-gray-100 flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                    <Eye className="h-5 w-5 mr-2 text-gray-700" /> View Response
                  </h3>
                  <button
                    onClick={() => setViewResponseModal({ show: false, response: '', query: null })}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200 flex-shrink-0"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                {/* Content */}
                <div className="flex-1 overflow-y-auto px-8 py-6">
                  <div className="space-y-4">
                    {viewResponseModal.query && (
                      <>
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50/50 p-5 rounded-xl border border-blue-200/50">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">Customer Query:</h4>
                          <p className="text-gray-900 text-base leading-relaxed break-words">{viewResponseModal.query.query}</p>
                        </div>
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50/50 p-5 rounded-xl border border-green-200/50">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">Response:</h4>
                          <div className="max-h-64 overflow-y-auto text-gray-900 text-base leading-relaxed break-words whitespace-pre-line">
                            {viewResponseModal.response}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {/* Footer */}
                <div className="flex-shrink-0 px-8 py-6 border-t border-gray-200 bg-gray-50/50 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setViewResponseModal({ show: false, response: '', query: null })}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    
      {/* Professional Notes Management Modal */}
      {notesModal.show && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full h-[90vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50/50">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                    <MessageSquare className="h-6 w-6 mr-3 text-blue-500" />
                    Notes Management
                  </h2>
                  <button
                    onClick={() => setNotesModal({ show: false, queryId: null, notes: [], newNote: "", submitting: false, editingNoteId: null, editingContent: "" })}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-gray-600 mt-2">Manage all notes for this support query</p>
              </div>

              {/* Side-by-side content */}
              <div className="flex-1 flex flex-row gap-6 overflow-hidden p-6">
                {/* Left: Add New Note (fixed width on md+, full width on small screens) */}
                <div className="w-full md:w-1/3 flex-shrink-0 bg-gray-50/50 border border-gray-100 rounded-xl p-5 flex flex-col">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <PlusCircle className="h-5 w-5 mr-2 text-green-500" />
                    Add New Note
                  </h3>

                  <textarea
                    value={notesModal.newNote}
                    onChange={(e) => setNotesModal(prev => ({ ...prev, newNote: e.target.value }))}
                    placeholder="Write your note here..."
                    rows={8}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 mb-4 flex-1"
                    disabled={notesModal.submitting}
                  />

                  <div className="flex items-center justify-between mt-2">
                    <div className="text-sm text-gray-500">Signed in as <span className="font-medium text-gray-700">{getCurrentUserName()}</span></div>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setNotesModal(prev => ({ ...prev, newNote: "" }))}
                         className="inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-all duration-200"
                        disabled={notesModal.submitting}
                      >
                        Clear
                      </button>

                      <button
                        onClick={handleAddNote}
                        disabled={!notesModal.newNote.trim() || notesModal.submitting}
                        className="inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {notesModal.submitting ? (
                          <>
                            <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white/30 border-t-white rounded-full inline-block"></div>
                            Adding...
                          </>
                        ) : (
                          <>
                            <PlusCircle className="h-4 w-4 flex-shrink-0" />
                            Add Note
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right: Existing Notes (scrollable) */}
                <div className="flex-1 flex flex-col overflow-hidden">
                  <div className="flex-shrink-0 px-4 py-2 border-b border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <MessageSquare className="h-5 w-5 mr-2 text-blue-500" />
                      All Notes ({notesModal.notes.length})
                    </h3>
                  </div>

                  <div className="flex-1 overflow-y-auto px-4 py-4">
                    {notesModal.notes.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="relative mx-auto w-20 h-20 mb-4">
                          <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl"></div>
                          <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center">
                            <MessageSquare className="h-8 w-8 text-gray-400" />
                          </div>
                        </div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">No Notes Yet</h4>
                        <p className="text-gray-500">Add your first note using the form on the left</p>
                      </div>
                    ) : (
                      <div className="space-y-4 pb-4">
                        {notesModal.notes.map((note, index) => (
                          <div key={note.id || index} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200">
                            <div className="p-5">
                              {/* Note Header */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-3">
                                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                                    <User className="h-4 w-4 text-white" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {note.added_by || "Unknown User"}
                                    </p>
                                    <div className="flex items-center text-xs text-gray-500">
                                      <Calendar className="h-3 w-3 mr-1" />
                                      {note.added_at ? new Date(note.added_at).toLocaleString() : "Unknown Date"}
                                      {note.updated_at && (
                                        <span className="ml-2 text-blue-600">(Updated: {new Date(note.updated_at).toLocaleString()})</span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Note Actions */}
                                <div className="flex items-center space-x-2">
                                  {notesModal.editingNoteId === note.id ? (
                                    <>
                                      <button
                                        onClick={() => handleSaveEditNote(note.id)}
                                        disabled={!notesModal.editingContent.trim() || notesModal.submitting}
                                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200 disabled:opacity-50"
                                        title="Save"
                                      >
                                        <Save className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={handleCancelEdit}
                                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                                        title="Cancel"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        onClick={() => handleEditNote(note.id, note.note)}
                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                                        title="Edit Note"
                                      >
                                        <Edit className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteNote(note.id)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                                        title="Delete Note"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Note Content */}
                              {notesModal.editingNoteId === note.id ? (
                                <textarea
                                  value={notesModal.editingContent}
                                  onChange={(e) => setNotesModal(prev => ({ ...prev, editingContent: e.target.value }))}
                                  rows={4}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                />
                              ) : (
                                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
                                    {note.note}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50/50 flex justify-end">
                <button
                  type="button"
                  onClick={() => setNotesModal({ show: false, queryId: null, notes: [], newNote: "", submitting: false, editingNoteId: null, editingContent: "" })}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg transition-all duration-200 transform hover:-translate-y-0.5 shadow-sm hover:shadow-md"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupportQueries;
